import { Box, Grid, Typography, Slider, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Maps, LoadingSpinner } from "@components";
import moment from "moment";

const RouteHistoryMap = (props) => {
	const { routeData, truckData, startDate, endDate, loading } = props;

	// SLIDER STATE
	const [sliderTimestamp, setSliderTimestamp] = useState(null);
	const [displayedRoute, setDisplayedRoute] = useState(null); // The Filtered Data for Map
	const [isPlaying, setIsPlaying] = useState(false);

	const minTime = moment(startDate).startOf('day').unix();
	const maxTime = moment(endDate).endOf('day').unix();

	// When new data arrives, reset slider to end
	useEffect(() => {
		if (routeData && Array.isArray(routeData) && routeData.length > 0) {
			const endUnix = moment(endDate).endOf('day').unix();
			setSliderTimestamp(endUnix);
		} else {
			setDisplayedRoute(null);
		}
	}, [routeData, startDate, endDate]);

	// Filter route when slider or data changes
	useEffect(() => {
		if (!routeData || !Array.isArray(routeData)) {
			setDisplayedRoute(null);
			return;
		}

		if (sliderTimestamp === null) {
			setDisplayedRoute(routeData);
			return;
		}

		// FILTER LOGIC
		const filtered = routeData.filter(point => parseInt(point.timestamp) <= sliderTimestamp);

		if (filtered.length > 0) {
			const lastPoint = filtered[filtered.length - 1];
			// Attach direction from last point so CustomMaps can rotate the truck icon
			filtered.direction = lastPoint.direction || 0;
			filtered.lat = lastPoint.lat;
			filtered.lng = lastPoint.lng;

			setDisplayedRoute(filtered);
		} else {
			setDisplayedRoute(null);
		}
	}, [sliderTimestamp, routeData]);

	// PLAYBACK EFFECT
	useEffect(() => {
		let interval;
		if (isPlaying && routeData && routeData.length > 0) {
			const effectiveMax = parseInt(routeData[routeData.length - 1].timestamp);
			const effectiveMin = parseInt(routeData[0].timestamp);

			interval = setInterval(() => {
				setSliderTimestamp((prev) => {
					const nextVal = (prev === null ? effectiveMin : prev) + 60; 
					if (nextVal >= effectiveMax) {
						setIsPlaying(false);
						return effectiveMax;
					}
					return nextVal;
				});
			}, 100);
		}
		return () => clearInterval(interval);
	}, [isPlaying, routeData]);

	const handleSliderChange = (event, newValue) => {
		setSliderTimestamp(newValue);
	};

	const handlePlayPause = () => {
		setIsPlaying(!isPlaying);
	};

	const handleReset = () => {
		setSliderTimestamp(maxTime);
		setIsPlaying(false);
	};

	const formatTimeLabel = (unix) => {
		return moment.unix(unix).format("hh:mm A");
	};

	if (loading) return <LoadingSpinner />;
	if (!routeData || routeData.length === 0) return <Box sx={{ p: 4, textAlign: 'center' }}>No route data available for the selected period.</Box>;

	return (
		<Grid container spacing={2}>
			{/* TIME SLIDER SECTION */}
			<Grid item xs={12} sx={{ px: 2, py: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
					<Typography variant="body2" sx={{ minWidth: 60, fontWeight: 700 }}>
						{moment.unix(minTime).format("HH:mm")}
					</Typography>

					<Slider
						value={sliderTimestamp || maxTime}
						min={minTime}
						max={maxTime}
						step={60} 
						onChange={handleSliderChange}
						valueLabelDisplay="auto"
						valueLabelFormat={formatTimeLabel}
						sx={{ flexGrow: 1 }}
					/>

					<Typography variant="body2" sx={{ minWidth: 60, fontWeight: 700 }}>
						{moment.unix(maxTime).format("HH:mm")}
					</Typography>

					<Button variant="contained" size="small" onClick={handlePlayPause} color={isPlaying ? "secondary" : "primary"} sx={{ borderRadius: 2 }}>
						{isPlaying ? "Pause" : "Play Route"}
					</Button>

					<Button variant="outlined" size="small" onClick={handleReset} sx={{ borderRadius: 2 }}>
						Reset
					</Button>
				</Box>
				<Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1, color: 'text.secondary', fontWeight: 600 }}>
					Current Playback Time: <strong>{sliderTimestamp ? formatTimeLabel(sliderTimestamp) : formatTimeLabel(maxTime)}</strong>
				</Typography>
			</Grid>

			<Grid item xs={12}>
				<Box sx={{ height: 500, borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
					<Maps
						data={truckData}
						polygonLine={displayedRoute || routeData}
						id={moment().unix()}
					/>
				</Box>
			</Grid>
		</Grid>
	);
};

export default RouteHistoryMap;
